import { type ReactNode } from "react";
import { ExportDataModal } from "~/molecules/ExportDataModal";
import { DownloadDatabaseModal } from "~/organisms/library/DownloadDatabaseModal";
import HeadingPage from "~/molecules/HeadingPage";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import { useTranslation } from "react-i18next";
import { NewDocumentButton } from "~/molecules/NewDocumentButton";
import { Button } from "~/atoms/button";
import { TagsModal } from "~/organisms/TagsModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "~/atoms/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { DocumentsList } from "~/organisms/library/DocumentsList";
import { useDocumentsListModel } from "~/hooks/useDocumentsListModel";

export const Library = () => {
  const { user } = useUserContext();
  const { t } = useTranslation();
  const documentListModel = useDocumentsListModel();

  const isReader = user.role === PlatformRole.reader;

  const renderActionMenuItem = (label: ReactNode) => (
    <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
      {label}
    </DropdownMenuItem>
  );

  return (
    <div className="space-y-4 px-6 py-7">
      <div className="flex justify-between">
        <div>
          <HeadingPage title={t(($) => $.library.header)} />
          <p className="text-muted-foreground">
            {t(($) => $.commons.workflow.browseDescription)}
          </p>
        </div>
        <div className="flex items-end gap-2">
          {!isReader && (
            <>
              <NewDocumentButton />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DownloadDatabaseModal
                    trigger={renderActionMenuItem(
                      t(($) => $.commons.download.documents),
                    )}
                    documents={documentListModel.filteredDocuments}
                  />
                  <ExportDataModal
                    trigger={renderActionMenuItem(
                      t(($) => $.commons.workflow.downloadReport),
                    )}
                    documents={documentListModel.filteredDocuments}
                  />
                  {(user.role === PlatformRole.coordinator ||
                    user.role === PlatformRole.superAdmin) && (
                    <TagsModal
                      trigger={renderActionMenuItem(
                        t(($) => $.modal.tags.manageTags),
                      )}
                    />
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      <DocumentsList model={documentListModel} />
    </div>
  );
};
